import React, { useState, useEffect, useCallback, useRef } from 'react';
import { B } from '../../constants';
import { borderRadius, transitions, breakpoints } from '../../designTokens';

const radiusMap = {
  xs: borderRadius.xs,
  sm: borderRadius.sm,
  md: borderRadius.md,
  lg: borderRadius.lg,
  xl: borderRadius.xl,
  pill: borderRadius.pill,
};

const sizeMap = {
  sm: { py: 6, px: 14, fs: 12, gap: 6 },
  md: { py: 8, px: 20, fs: 13, gap: 8 },
  lg: { py: 12, px: 28, fs: 14, gap: 10 },
};

const iconPositionMap = {
  left: { flexDirection: 'row', gap: 6 },
  right: { flexDirection: 'row-reverse', gap: 6 },
  top: { flexDirection: 'column', gap: 4 },
  bottom: { flexDirection: 'column-reverse', gap: 4 },
};

function TabBadge({ count, isActive, badgeColor, badgeVariant }) {
  const bg = badgeVariant === 'outline' ? 'transparent'
    : badgeVariant === 'soft' ? `${(badgeColor || B.forest)}18`
    : isActive ? `${(badgeColor || B.forest)}18` : '#f0f0f0';
  const clr = badgeColor || (isActive ? B.forest : B.muted);
  const border = badgeVariant === 'outline' ? `1.5px solid ${clr}` : 'none';
  return (
    <span style={{
      marginLeft: 8, background: bg, color: clr,
      padding: '2px 8px', borderRadius: borderRadius.pill,
      fontSize: 11, border, lineHeight: 1.4,
    }}>
      {count}
    </span>
  );
}

function TabScrollButton({ direction, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={direction === 'left' ? 'Scroll tabs left' : 'Scroll tabs right'}
      style={{
        position: 'sticky', [direction]: 0, zIndex: 2,
        width: 28, height: '100%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'white', border: 'none', cursor: 'pointer',
        fontSize: 16, color: B.muted, boxShadow: direction === 'left'
          ? '4px 0 8px rgba(0,0,0,0.06)' : '-4px 0 8px rgba(0,0,0,0.06)',
      }}
    >
      {direction === 'left' ? '◀' : '▶'}
    </button>
  );
}

export default function Tab({
  tabs,
  activeTab,
  onChange,
  variant = 'underline',
  size = 'md',
  orientation = 'horizontal',
  animatedIndicator = true,
  scrollable,
  indicatorColor = B.forest,
  indicatorHeight = 3,
  showContent,
  radius,
  fullWidth,
  tabStyle,
  onActiveChange,
  idPrefix = 'tab',
  iconPosition = 'left',
  transitionDuration = 'fast',
  rightAction,
  closable,
  onClose,
  lazy,
  validateTab,
  draggable,
  responsiveOrientation,
}) {
  const [activeOrientation, setActiveOrientation] = useState(orientation);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [showScrollButtons, setShowScrollButtons] = useState({ left: false, right: false });
  const [dragIndex, setDragIndex] = useState(null);
  const [localTabs, setLocalTabs] = useState(tabs);
  const tabsRef = useRef({});
  const scrollRef = useRef(null);

  useEffect(() => {
    setLocalTabs(tabs);
  }, [tabs]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!responsiveOrientation) { setActiveOrientation(orientation); return; }
    const check = () => {
      const w = window.innerWidth;
      const bp = Object.keys(breakpoints)
        .sort((a, b) => parseFloat(breakpoints[a]) - parseFloat(breakpoints[b]));
      let matched = orientation;
      for (const key of bp) {
        if (w >= parseFloat(breakpoints[key]) && responsiveOrientation[key]) {
          matched = responsiveOrientation[key];
        }
      }
      setActiveOrientation(matched);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [orientation, responsiveOrientation]);

  const isVertical = activeOrientation === 'vertical';
  const s = sizeMap[size] || sizeMap.md;
  const tVal = transitions[transitionDuration] || transitionDuration || transitions.fast;

  useEffect(() => {
    if (!animatedIndicator || variant !== 'underline' || !tabsRef.current[activeTab]) return;
    const el = tabsRef.current[activeTab];
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const parentRect = el.parentElement?.getBoundingClientRect();
      if (!parentRect) return;
      if (isVertical) {
        setIndicatorStyle({
          height: indicatorHeight,
          width: rect.width,
          transform: `translateY(${rect.top - parentRect.top}px)`,
          transition: reducedMotion ? 'none' : `all ${tVal}`,
        });
      } else {
        setIndicatorStyle({
          height: indicatorHeight,
          width: rect.width,
          transform: `translateX(${rect.left - parentRect.left}px)`,
          transition: reducedMotion ? 'none' : `all ${tVal}`,
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [activeTab, animatedIndicator, variant, isVertical, indicatorHeight, tVal, reducedMotion]);

  useEffect(() => {
    if (!scrollable || !scrollRef.current) return;
    const el = scrollRef.current;
    const check = () => {
      setShowScrollButtons({
        left: el.scrollLeft > 10,
        right: el.scrollLeft < el.scrollWidth - el.clientWidth - 10,
      });
    };
    check();
    el.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    return () => {
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [scrollable]);

  const scrollTabs = useCallback((dir) => {
    if (!scrollRef.current) return;
    const amount = dir === 'left' ? -200 : 200;
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  }, []);

  const handleTabChange = useCallback((tabId, tab) => {
    if (tab?.disabled) return;
    if (validateTab) {
      const allowed = validateTab(tabId, tab);
      if (allowed === false) return;
    }
    onChange?.(tabId);
    onActiveChange?.(tabId, tab);
  }, [onChange, onActiveChange, validateTab]);

  const handleKeyDown = useCallback((e, tabId, tab, index) => {
    if (tab?.disabled) return;
    const tabIds = (draggable ? localTabs : tabs).filter(t => !t.disabled).map(t => t.id);
    const currentIdx = tabIds.indexOf(tabId);
    let nextIdx = -1;
    if (isVertical) {
      if (e.key === 'ArrowDown') nextIdx = currentIdx + 1;
      else if (e.key === 'ArrowUp') nextIdx = currentIdx - 1;
    } else {
      if (e.key === 'ArrowRight') nextIdx = currentIdx + 1;
      else if (e.key === 'ArrowLeft') nextIdx = currentIdx - 1;
    }
    if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = tabIds.length - 1;
    if (nextIdx >= 0 && nextIdx < tabIds.length) {
      e.preventDefault();
      const nextId = tabIds[nextIdx];
      handleTabChange(nextId, (draggable ? localTabs : tabs).find(t => t.id === nextId));
      tabsRef.current[nextId]?.focus();
    }
  }, [tabs, localTabs, isVertical, handleTabChange, draggable]);

  const handleDragStart = useCallback((e, index) => {
    if (!draggable) return;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }, [draggable]);

  const handleDragOver = useCallback((e, index) => {
    if (!draggable || dragIndex === null || dragIndex === index) return;
    e.preventDefault();
    const reordered = [...localTabs];
    const [item] = reordered.splice(dragIndex, 1);
    reordered.splice(index, 0, item);
    setLocalTabs(reordered);
    setDragIndex(index);
  }, [draggable, dragIndex, localTabs]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    onChange?.(localTabs.find(t => t.id === activeTab)?.id);
  }, [localTabs, activeTab, onChange]);

  const activeTabs = draggable ? localTabs : tabs;

  const tabListStyle = {
    display: isVertical ? 'flex' : 'flex',
    flexDirection: isVertical ? 'column' : 'row',
    gap: variant === 'pill' ? 4 : 0,
    background: variant === 'pill' ? '#f5f5f5' : 'transparent',
    padding: variant === 'pill' ? 4 : 0,
    borderRadius: variant === 'pill' ? borderRadius.pill : 0,
    position: 'relative',
    width: fullWidth ? '100%' : undefined,
    overflow: scrollable && !isVertical ? 'auto' : undefined,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    flexWrap: 'nowrap',
    alignItems: isVertical ? 'stretch' : 'center',
    ...(variant === 'enclosed' ? {
      borderBottom: `2px solid #e5e0d8`,
    } : {}),
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: isVertical ? 'row' : 'column',
    alignItems: isVertical ? 'flex-start' : 'stretch',
    gap: isVertical ? 16 : 0,
    width: fullWidth ? '100%' : undefined,
  };

  return (
    <div style={containerStyle}>
      <div style={{
        display: 'flex',
        flexDirection: isVertical ? 'column' : 'row',
        alignItems: 'center',
        width: fullWidth ? '100%' : undefined,
        position: 'relative',
      }}>
        {scrollable && !isVertical && showScrollButtons.left && (
          <TabScrollButton direction="left" onClick={() => scrollTabs('left')} />
        )}
        <div
          ref={scrollRef}
          style={tabListStyle}
          role="tablist"
          aria-orientation={activeOrientation}
        >
          {variant === 'underline' && animatedIndicator && !reducedMotion && (
            <div
              style={{
                position: 'absolute', bottom: 0,
                background: indicatorColor,
                borderRadius: indicatorHeight,
                zIndex: 1,
                ...indicatorStyle,
              }}
            />
          )}
          {activeTabs.map((tab, index) => {
            const isActive = activeTab === tab.id;
            const effectiveRadius = radius
              ? radiusMap[radius] || radius
              : variant === 'pill' ? borderRadius.pill : 0;

            const baseStyle = {
              background: variant === 'pill' && isActive ? 'white'
                : variant === 'enclosed' && isActive ? 'white'
                : variant === 'soft' && isActive ? `${B.forest}12`
                : variant === 'line' ? 'transparent'
                : 'none',
              border: variant === 'enclosed'
                ? `2px solid ${isActive ? '#e5e0d8' : 'transparent'}`
                : variant === 'line' ? 'none'
                : 'none',
              borderBottom: variant === 'underline'
                ? `${indicatorHeight}px solid ${isActive && !animatedIndicator ? indicatorColor : 'transparent'}`
                : 'none',
              borderLeft: isVertical && variant === 'underline'
                ? `${indicatorHeight}px solid ${isActive && !animatedIndicator ? indicatorColor : 'transparent'}`
                : 'none',
              padding: `${s.py}px ${s.px}px`,
              borderRadius: effectiveRadius,
              cursor: tab.disabled ? 'not-allowed' : 'pointer',
              fontFamily: "'Montserrat',sans-serif",
              fontSize: s.fs,
              fontWeight: 600,
              color: tab.disabled ? '#ccc' : isActive ? B.forest : B.muted,
              boxShadow: variant === 'pill' && isActive ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              transition: reducedMotion ? 'none' : `all ${tVal}`,
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: s.gap,
              opacity: tab.disabled ? 0.4 : 1,
              flex: fullWidth ? 1 : undefined,
              outline: 'none',
              position: 'relative',
              userSelect: 'none',
              ...(tabStyle ? tabStyle(tab, isActive) : {}),
            };

            const iconPosStyle = tab.icon ? iconPositionMap[iconPosition] || iconPositionMap.left : {};

            return (
              <button
                key={tab.id}
                ref={(el) => { tabsRef.current[tab.id] = el; }}
                role="tab"
                aria-selected={isActive}
                aria-controls={`${idPrefix}-panel-${tab.id}`}
                id={`${idPrefix}-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                disabled={tab.disabled}
                onClick={() => handleTabChange(tab.id, tab)}
                onKeyDown={(e) => handleKeyDown(e, tab.id, tab, index)}
                draggable={draggable}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  ...baseStyle,
                  ...(tab.icon ? { ...iconPosStyle } : {}),
                }}
              >
                {tab.icon && <span style={{ display: 'inline-flex', flexShrink: 0, fontSize: s.fs + 2 }}>{tab.icon}</span>}
                {tab.label && <span>{tab.label}</span>}
                {tab.count !== undefined && (
                  <TabBadge
                    count={tab.count}
                    isActive={isActive}
                    badgeColor={tab.badgeColor}
                    badgeVariant={tab.badgeVariant}
                  />
                )}
                {closable && tab.closable !== false && (
                  <span
                    onClick={(e) => { e.stopPropagation(); onClose?.(tab.id); }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Close ${tab.label}`}
                    style={{
                      marginLeft: 4, fontSize: 12, cursor: 'pointer',
                      color: B.muted, opacity: 0.6, lineHeight: 1,
                    }}
                  >
                    ✕
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {rightAction && (
          <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
            {rightAction}
          </div>
        )}
        {scrollable && !isVertical && showScrollButtons.right && (
          <TabScrollButton direction="right" onClick={() => scrollTabs('right')} />
        )}
      </div>
      {showContent && (
        <div
          role="tabpanel"
          id={`${idPrefix}-panel-${activeTab}`}
          aria-labelledby={`${idPrefix}-${activeTab}`}
          style={{
            paddingTop: isVertical ? 0 : 16,
            paddingLeft: isVertical ? 16 : 0,
            width: '100%',
          }}
        >
          {activeTabs.map(tab => {
            if (tab.id !== activeTab) return null;
            if (lazy && !tab.content) return null;
            return (
              <div key={tab.id}>
                {tab.content}
              </div>
            );
          })}
        </div>
      )}
      {variant === 'line' && (
        <div style={{
          width: '100%', height: 2, background: '#e5e0d8',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute', height: 2, background: indicatorColor,
            borderRadius: 1, transition: reducedMotion ? 'none' : `all ${tVal}`,
            ...(isVertical ? {} : indicatorStyle),
          }} />
        </div>
      )}
    </div>
  );
}
